/**
 * Colorblind Test Engine — window.CBT
 * ===================================
 * The scientific core of the /colorblind-test etalon. Everything here is built
 * on top of window.CVD (public/js/cvd-simulation.js — Brettel 1997 + Machado 2009
 * + CIEDE2000). We deliberately do NOT reimplement any dichromat math: the same
 * model that powers /colorblind-simulator, /colorblind-palette etc. is the single
 * source of truth, so a plate that "vanishes" here vanishes identically in the
 * simulator.
 *
 * Three test modules, each scientifically grounded and screen-honest:
 *
 *  1. ISHIHARA pseudoisochromatic plates — the digit and background dots are a
 *     colour pair that is SEPARATED for a normal trichromat (large CIEDE2000)
 *     yet COLLAPSED for the targeted deficiency (tiny cvdDistance), i.e. the two
 *     colours sit on that type's confusion line. Per-dot OKLCH-lightness jitter,
 *     applied IDENTICALLY to figure and ground dots, removes every luminance cue
 *     so the figure can only be read chromatically. Each plate is self-validated
 *     by re-simulating the rendered pixels and checking region MEANS in the
 *     chroma plane (a*,b*) — never per-pixel, because the intentional luminance
 *     noise makes per-pixel ΔE huge by design.
 *
 *  2. FARNSWORTH D-15 arrangement — equal-lightness / equal-chroma caps on a hue
 *     circle, scored with the Vingrys & King-Smith (1988) moment analysis in CIE
 *     1976 u'v': confusion angle → type, C-index → severity, TES = majR + minR.
 *
 *  3. TRIVECTOR discrimination — replaces the (pseudoscientific on RGB) anomalo-
 *     scope with a Cambridge-Colour-Test-style chromatic threshold along each of
 *     the protan/deutan/tritan confusion lines, embedded in luminance noise.
 *
 * Validity is SCREENING-grade on uncalibrated sRGB displays — never diagnosis.
 * See docs / FAQ copy. window.CVD is required; guard before use.
 */
(function () {
  'use strict';
  if (!window.CVD) { window.CBT = { ready: false }; return; }
  var CVD = window.CVD;

  /* ===================================================================== */
  /* sRGB gamma (CVD keeps these internal; we need the forward path for     */
  /* the u'v' → sRGB trivector stimulus and gamut tests).                   */
  /* ===================================================================== */
  function srgbToLinear(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function linearToSrgb01(c) { return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; }

  function angDiff(a, b) { var d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; }
  /** Seeded RNG so a plate can be redrawn EXACTLY as it was shown. */
  function makeRng(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  /* ===================================================================== */
  /* SECTION 1 — Colour pool (OKLCH, gamut-verified by round-trip)          */
  /* ===================================================================== */
  /* Mid-lightness, moderate-chroma colours sit cleanly on confusion lines  */
  /* and survive luminance jitter without clipping — this is why real       */
  /* plates look pastel/olive, never neon. oklchToRgb silently clamps out-   */
  /* of-gamut requests, so we round-trip and reject anything that moved.     */
  var POOL_L = 0.62;
  var POOL_CHROMAS = [0.07, 0.10, 0.13];
  var POOL_HSTEP = 2;

  function buildPool(L, chromas, hStep) {
    L = L || POOL_L; chromas = chromas || POOL_CHROMAS; hStep = hStep || POOL_HSTEP;
    var pool = [];
    for (var ci = 0; ci < chromas.length; ci++) {
      var C = chromas[ci];
      for (var h = 0; h < 360; h += hStep) {
        var rgb = CVD.oklchToRgb(L, C, h);
        var back = CVD.rgbToOklch(rgb[0], rgb[1], rgb[2]);
        if (Math.abs(back[0] - L) < 0.012 && Math.abs(back[1] - C) < 0.012 && angDiff(back[2], h) < 2.5) {
          pool.push({ rgb: rgb, L: L, C: C, h: h, lab: CVD.rgbToLab(rgb[0], rgb[1], rgb[2]) });
        }
      }
    }
    return pool;
  }

  /* Precompute, for every pool colour, its simulated CIE-Lab under each type   */
  /* at full and half severity. cvdDistance(i,j,type) then reduces to a single  */
  /* deltaE2000 on these cached Labs — turning the O(n^2) pair scan from        */
  /* thousands of Brettel sims into cheap colour-difference lookups.            */
  var RG_TYPES = ['protan', 'deutan', 'tritan'];
  function precompute(pool) {
    for (var k = 0; k < pool.length; k++) {
      var c = pool[k];
      c.sim1 = {}; c.sim05 = {};
      for (var t = 0; t < RG_TYPES.length; t++) {
        var ty = RG_TYPES[t];
        var s1 = CVD.simulate(c.rgb[0], c.rgb[1], c.rgb[2], ty, 1.0);
        c.sim1[ty] = CVD.rgbToLab(s1[0], s1[1], s1[2]);
        var s5 = CVD.simulate(c.rgb[0], c.rgb[1], c.rgb[2], ty, 0.5);
        c.sim05[ty] = CVD.rgbToLab(s5[0], s5[1], s5[2]);
      }
    }
    return pool;
  }
  function cvd1(a, b, type) { return CVD.deltaE2000(a.sim1[type], b.sim1[type]); }
  function cvd05(a, b, type) { return CVD.deltaE2000(a.sim05[type], b.sim05[type]); }
  function normalDE(a, b) { return CVD.deltaE2000(a.lab, b.lab); }

  /* ===================================================================== */
  /* SECTION 2 — Plate colour-pair selection (confusion-line projection)    */
  /* ===================================================================== */
  /* Thresholds from the verified blueprint (CIEDE2000 units):              */
  var NORMAL_MIN = 18;     // figure must be obvious to a trichromat
  var CVD_MAX = 4;         // figure must vanish for the type at full severity
  var CVD_MAX_MILD = 8;    // ...and still collapse for a moderate (0.5) case
  var HIDDEN_NORMAL_MAX = 7; // hidden-digit: near-invisible to normal vision
  var HIDDEN_CVD_MIN = 14;   // ...but clearly separated for the type
  var DIFF_OTHER_MIN = 10;   // differentiating: the OTHER r-g type keeps it visible

  // VANISHING: normal sees the digit, the target type does not.
  function findVanishing(pool, type) {
    var out = [];
    for (var i = 0; i < pool.length; i++) {
      for (var j = i + 1; j < pool.length; j++) {
        var nd = normalDE(pool[i], pool[j]);
        if (nd < NORMAL_MIN) continue;
        var c1 = cvd1(pool[i], pool[j], type);
        if (c1 > CVD_MAX) continue;
        var c5 = cvd05(pool[i], pool[j], type);
        if (c5 > CVD_MAX_MILD) continue;
        out.push({ fg: pool[i], bg: pool[j], normalDE: nd, cvd1: c1, cvd05: c5, fitness: nd - 6 * c1 });
      }
    }
    out.sort(function (a, b) { return b.fitness - a.fitness; });
    return out;
  }

  // HIDDEN-DIGIT: invisible to normal vision, visible to the target type.
  function findHidden(pool, type) {
    var out = [];
    for (var i = 0; i < pool.length; i++) {
      for (var j = i + 1; j < pool.length; j++) {
        var nd = normalDE(pool[i], pool[j]);
        if (nd > HIDDEN_NORMAL_MAX) continue;
        var c1 = cvd1(pool[i], pool[j], type);
        if (c1 < HIDDEN_CVD_MIN) continue;
        out.push({ fg: pool[i], bg: pool[j], normalDE: nd, cvd1: c1, fitness: c1 - 4 * nd });
      }
    }
    out.sort(function (a, b) { return b.fitness - a.fitness; });
    return out;
  }

  // DEMONSTRATION: separated for EVERYONE (all three types + normal).
  function findDemonstration(pool) {
    var out = [];
    for (var i = 0; i < pool.length; i++) {
      for (var j = i + 1; j < pool.length; j++) {
        var nd = normalDE(pool[i], pool[j]);
        if (nd < NORMAL_MIN) continue;
        var cp = cvd1(pool[i], pool[j], 'protan');
        var cd = cvd1(pool[i], pool[j], 'deutan');
        var ct = cvd1(pool[i], pool[j], 'tritan');
        var minC = Math.min(cp, cd, ct);
        if (minC < 15) continue;
        out.push({ fg: pool[i], bg: pool[j], normalDE: nd, minCvd: minC, fitness: minC + nd });
      }
    }
    out.sort(function (a, b) { return b.fitness - a.fitness; });
    return out;
  }

  // DIFFERENTIATING: a pair confusable by `type` but not by the other r-g type.
  function findDifferentiating(pool, type) {
    var other = type === 'protan' ? 'deutan' : 'protan';
    var out = [];
    for (var i = 0; i < pool.length; i++) {
      for (var j = i + 1; j < pool.length; j++) {
        var nd = normalDE(pool[i], pool[j]);
        if (nd < NORMAL_MIN) continue;
        var c1 = cvd1(pool[i], pool[j], type);
        if (c1 > 6) continue;                 // relaxed per blueprint (lines near-parallel)
        var co = cvd1(pool[i], pool[j], other);
        if (co < DIFF_OTHER_MIN) continue;
        out.push({ fg: pool[i], bg: pool[j], normalDE: nd, cvdTarget: c1, cvdOther: co, fitness: co - c1 });
      }
    }
    out.sort(function (a, b) { return b.fitness - a.fitness; });
    return out;
  }

  /* Build the full validated spec set once (offline, via Playwright on the   */
  /* real window.CVD). The result is frozen into PLATE_SPECS below so runtime  */
  /* never pays the scan. Returns plain {fg:[r,g,b],bg:[r,g,b],...} records.    */
  function computeSpecs() {
    var pool = precompute(buildPool());
    function top(list, n) {
      return list.slice(0, n).map(function (p) {
        return {
          fg: p.fg.rgb, bg: p.bg.rgb,
          fgHex: CVD.rgbToHex(p.fg.rgb[0], p.fg.rgb[1], p.fg.rgb[2]),
          bgHex: CVD.rgbToHex(p.bg.rgb[0], p.bg.rgb[1], p.bg.rgb[2]),
          normalDE: round2(p.normalDE),
          cvd1: p.cvd1 != null ? round2(p.cvd1) : undefined,
          cvd05: p.cvd05 != null ? round2(p.cvd05) : undefined,
          cvdTarget: p.cvdTarget != null ? round2(p.cvdTarget) : undefined,
          cvdOther: p.cvdOther != null ? round2(p.cvdOther) : undefined,
          minCvd: p.minCvd != null ? round2(p.minCvd) : undefined
        };
      });
    }
    return {
      poolSize: pool.length,
      demonstration: top(findDemonstration(pool), 4),
      vanish_protan: top(findVanishing(pool, 'protan'), 8),
      vanish_deutan: top(findVanishing(pool, 'deutan'), 8),
      vanish_tritan: top(findVanishing(pool, 'tritan'), 8),
      hidden_deutan: top(findHidden(pool, 'deutan'), 4),
      hidden_protan: top(findHidden(pool, 'protan'), 4),
      diff_protan: top(findDifferentiating(pool, 'protan'), 4),
      diff_deutan: top(findDifferentiating(pool, 'deutan'), 4)
    };
  }
  function round2(x) { return Math.round(x * 100) / 100; }

  /* ===================================================================== */
  /* SECTION 3 — Validated plate specs (frozen from computeSpecs())         */
  /* ===================================================================== */
  /* Frozen from the offline Playwright run against the real window.CVD       */
  /* (tests/playwright/cbt-engine-scratch.mjs → _cbt/specs.json). Every pair   */
  /* below was rendered and re-simulated per-pixel to confirm the digit        */
  /* collapses for its type and stays visible to normal vision. Runtime never  */
  /* re-scans the pool. computeSpecs() remains for transparency/regeneration.  */
  var PLATE_SPECS = {
    poolSize: 516,
    demonstration: [
      { fg: [176, 122, 0], bg: [144, 115, 202] },
      { fg: [176, 122, 0], bg: [139, 117, 204] },
      { fg: [175, 124, 0], bg: [144, 115, 202] }
    ],
    vanish_protan: [
      { fg: [166, 124, 91], bg: [128, 141, 92] },
      { fg: [97, 139, 174], bg: [146, 123, 167] },
      { fg: [165, 125, 90], bg: [127, 141, 93] },
      { fg: [163, 126, 88], bg: [128, 141, 92] },
      { fg: [99, 139, 174], bg: [147, 123, 166] },
      { fg: [164, 125, 89], bg: [127, 141, 93] }
    ],
    vanish_deutan: [
      { fg: [179, 118, 70], bg: [131, 142, 67] },
      { fg: [193, 110, 47], bg: [153, 135, 0] },
      { fg: [171, 124, 60], bg: [111, 148, 54] },
      { fg: [191, 112, 40], bg: [142, 139, 15] },
      { fg: [193, 110, 47], bg: [150, 136, 0] },
      { fg: [168, 123, 94], bg: [128, 141, 92] }
    ],
    vanish_tritan: [],
    hidden_deutan: [
      { fg: [103, 137, 176], bg: [90, 132, 212] },
      { fg: [103, 137, 176], bg: [86, 133, 212] }
    ],
    hidden_protan: [
      { fg: [102, 138, 175], bg: [86, 133, 212] }
    ],
    /* BLUE-YELLOW reference plates (added 2026-09-05). Every other plate here
       is red-green, and that is a blind spot rather than a focus: "failed the
       plates" cannot separate a red-green deficiency from vision that is simply
       duller all round — a dim phone in daylight, an older observer — and on the
       bench that ambiguity was worth ~10 points of sensitivity. These three are
       carried by the S-cone channel, so anyone with red-green deficiency reads
       them AS EASILY AS A NORMAL (d′ 9.7 / 11.6 / 14.0 for protan, deutan and
       normal alike) while they vanish completely for tritan (d′ 0.0). The
       difficulties are matched to the WEAK END of the red-green set (9.5-11),
       not to its middle: a duller-than-average observer fails the weakest
       plates first, so a reference pitched above them is read by everyone and
       separates nothing — the first attempt at this, pitched at 13-17, left
       "duller eyes" indistinguishable from deuteranomaly on the bench. They are also the only tritan
       screening the plate stage has ever had. */
    blue_yellow: [
      { fg: [217, 147, 173], bg: [211, 162, 59] },
      { fg: [237, 144, 94], bg: [243, 122, 197] },
      { fg: [204, 163, 104], bg: [213, 141, 215] }
    ],
    diff_protan: [
      { fg: [49, 141, 206], bg: [170, 106, 180] },
      { fg: [0, 145, 200], bg: [174, 104, 176] }
    ],
    diff_deutan: [
      { fg: [80, 148, 142], bg: [189, 99, 151] },
      { fg: [39, 153, 141], bg: [189, 99, 151] }
    ]
  };

  /* The fixed plate sequence shown to the user. Each entry references a spec   */
  /* bucket; the actual colours are chosen from the validated list at render.   */
  /*                                                                            */
  /* ⚠️ The HIDDEN-digit plate was removed on 2026-09-05. It is supposed to be   */
  /* invisible to normal vision and readable by a deficient observer, and it was */
  /* neither: measured against cone physiology a normal observer sees d′ 6.9 on  */
  /* it and a deuteranope 6.0 — a coin flip for both. Worse, `ishTally` counted  */
  /* a CORRECT reading of it as evidence of red-green deficiency, so a normal    */
  /* who guessed it right was pushed toward a diagnosis. It cannot be repaired   */
  /* either: a dichromat's channels are a degeneration of a normal's, so any     */
  /* figure they can see, a normal can see too — the classic hidden plate leans  */
  /* on a residual LUMINANCE cue, which our per-dot lightness jitter destroys    */
  /* by design.                                                                  */
  /*                                                                            */
  /* `readableBy` lists who SHOULD read the digit; a miss is evidence for the    */
  /* types not listed. Note that protan and deutan are listed together on every  */
  /* red-green plate: the two "differentiating" plates do not in fact            */
  /* differentiate (measured: xp collapses to d′ 2.4 for protan AND 4.8 for      */
  /* deutan), because the two confusion lines are too close for an isoluminant   */
  /* plate to separate. What separates them is the trivector stage.              */
  /*                                                                            */
  /* Ishihara plates are RED-GREEN only — exactly like the physical Ishihara    */
  /* test, and because the Brettel tritan model in this codebase cannot produce */
  /* a fully-collapsing tritan plate on sRGB (verified: zero pairs reach        */
  /* cvdDistance<=4). Tritan is screened instead by the D-15 confusion angle    */
  /* and the trivector test. `readableBy` lists who SHOULD read the digit; a    */
  /* miss is evidence for the types NOT listed.                                 */
  var PLATE_SEQUENCE = [
    { id: 'demo', shape: 'circle', digit: '12', bucket: 'demonstration', pair: 0, kind: 'demo',   readableBy: ['normal', 'protan', 'deutan', 'tritan'] },
    { id: 'd1',   shape: 'triangle', digit: '8',  bucket: 'vanish_deutan', pair: 0, kind: 'vanish', type: 'deutan', readableBy: ['normal', 'tritan'] },
    { id: 'p1',   shape: 'square', digit: '6',  bucket: 'vanish_protan', pair: 0, kind: 'vanish', type: 'protan', readableBy: ['normal', 'tritan'] },
    { id: 'y1',   shape: 'star', digit: '7',  bucket: 'blue_yellow',   pair: 0, kind: 'yb',     type: 'tritan', readableBy: ['normal', 'protan', 'deutan'] },
    { id: 'd2',   shape: 'heart', digit: '29', bucket: 'vanish_deutan', pair: 1, kind: 'vanish', type: 'deutan', readableBy: ['normal', 'tritan'] },
    { id: 'p2',   shape: 'cross', digit: '5',  bucket: 'vanish_protan', pair: 1, kind: 'vanish', type: 'protan', readableBy: ['normal', 'tritan'] },
    { id: 'y2',   shape: 'triangle', digit: '45', bucket: 'blue_yellow',   pair: 1, kind: 'yb',     type: 'tritan', readableBy: ['normal', 'protan', 'deutan'] },
    { id: 'd3',   shape: 'star', digit: '74', bucket: 'vanish_deutan', pair: 2, kind: 'vanish', type: 'deutan', readableBy: ['normal', 'tritan'] },
    { id: 'xp',   shape: 'square', digit: '16', bucket: 'diff_protan',   pair: 0, kind: 'vanish', type: 'protan', readableBy: ['normal', 'tritan'] },
    { id: 'y3',   shape: 'heart', digit: '2',  bucket: 'blue_yellow',   pair: 2, kind: 'yb',     type: 'tritan', readableBy: ['normal', 'protan', 'deutan'] },
    { id: 'xd',   shape: 'circle', digit: '73', bucket: 'diff_deutan',   pair: 0, kind: 'vanish', type: 'deutan', readableBy: ['normal', 'tritan'] },
    { id: 'rg',   shape: 'cross', digit: '3',  bucket: 'vanish_protan', pair: 2, kind: 'vanish', type: 'protan', readableBy: ['normal', 'tritan'] }
  ];

  var _specCache = null;
  function getSpecs() {
    if (PLATE_SPECS) return PLATE_SPECS;
    if (!_specCache) _specCache = computeSpecs();   // dev fallback
    return _specCache;
  }

  /* Choose a concrete colour pair for a plate.                                */
  /* ⚠️ The index is the entry's own `pair`, NOT its position in the sequence.  */
  /* Position-based indexing silently collapsed plates onto the same colours:   */
  /* with three blue-yellow pairs at sequence positions 3, 6 and 9, every one   */
  /* of them resolved to list[0] (3%3 = 6%3 = 9%3 = 0), so the three "different */
  /* difficulties" were one plate shown three times — and the same arithmetic   */
  /* had d1 and d3 sharing a pair before that. Found by the scoring guard.      */
  function pickPair(bucket, idx) {
    var specs = getSpecs();
    var list = specs[bucket];
    if (!list || !list.length) return null;
    return list[idx % list.length];
  }

  /* ===================================================================== */
  /* SECTION 4 — Glyph rasterization → boolean figure mask                  */
  /* ===================================================================== */
  /**
   * The figure can be a DIGIT or a SHAPE. Shapes exist because the digit-only
   * set locks out everyone who cannot read numerals — children, and adults for
   * whom the numeral is the harder half of the task — and because the plates
   * people in Russia recognise (Rabkin's) are half shapes. The scoring is
   * untouched: the same colour pair carries the figure either way, only the
   * answer alphabet changes, and the guess rate that goes with it travels with
   * each answer so the verdict cannot mistake a lucky guess for reading.
   */
  var SHAPES = ['circle', 'triangle', 'square', 'star', 'heart', 'cross'];

  function drawShape(ctx, shape, size) {
    var c = size / 2, r = size * 0.30, i, a, rr;
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(c, c, r, 0, Math.PI * 2);
    } else if (shape === 'square') {
      ctx.rect(c - r * 0.88, c - r * 0.88, r * 1.76, r * 1.76);
    } else if (shape === 'triangle') {
      ctx.moveTo(c, c - r * 1.05);
      ctx.lineTo(c + r * 0.95, c + r * 0.72);
      ctx.lineTo(c - r * 0.95, c + r * 0.72);
      ctx.closePath();
    } else if (shape === 'star') {
      for (i = 0; i < 10; i++) {
        a = -Math.PI / 2 + i * Math.PI / 5;
        rr = (i % 2 === 0) ? r * 1.12 : r * 0.48;
        ctx[i ? 'lineTo' : 'moveTo'](c + Math.cos(a) * rr, c + Math.sin(a) * rr);
      }
      ctx.closePath();
    } else if (shape === 'heart') {
      var w = r * 1.05, h = r * 1.0;
      ctx.moveTo(c, c + h);
      ctx.bezierCurveTo(c - w * 1.6, c - h * 0.30, c - w * 0.62, c - h * 1.42, c, c - h * 0.42);
      ctx.bezierCurveTo(c + w * 0.62, c - h * 1.42, c + w * 1.6, c - h * 0.30, c, c + h);
      ctx.closePath();
    } else {   // cross
      var t = r * 0.40;
      ctx.rect(c - t, c - r, t * 2, r * 2);
      ctx.rect(c - r, c - t, r * 2, t * 2);
    }
    ctx.fill();
  }

  /** `figure` is a digit string, or {shape:'triangle'}. */
  function buildMask(figure, size) {
    var c = document.createElement('canvas');
    c.width = size; c.height = size;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = '#000';
    if (figure && typeof figure === 'object' && figure.shape) {
      drawShape(ctx, figure.shape, size);
    } else {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Bold rounded sans, high x-height; thick stroke so >=3 dot rows span a stroke.
      var fs = Math.round(size * 0.60);
      ctx.font = '900 ' + fs + 'px "Arial Rounded MT Bold", Arial, Helvetica, sans-serif';
      ctx.lineWidth = Math.round(size * 0.05);
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000';
      ctx.strokeText(String(figure), size / 2, size / 2);
      ctx.fillText(String(figure), size / 2, size / 2);
    }
    var data = ctx.getImageData(0, 0, size, size).data;
    return { data: data, size: size };
  }
  function maskHit(mask, x, y) {
    var px = Math.round(x), py = Math.round(y);
    if (px < 0 || px >= mask.size || py < 0 || py >= mask.size) return false;
    return mask.data[(py * mask.size + px) * 4 + 3] > 128;  // alpha
  }

  /* ===================================================================== */
  /* SECTION 5 — Dot packing (dart-throwing, mixed radii)                   */
  /* ===================================================================== */
  function packDots(D, rng) {
    rng = rng || Math.random;
    var cx = D / 2, cy = D / 2, R = D / 2 - 2;
    var rMin = 0.011 * D, rMax = 0.028 * D, gap = 1.5;
    var dots = [];
    var grid = {}, cell = rMax * 2 + gap;
    function key(gx, gy) { return gx + ',' + gy; }
    var attempts = 0, maxAttempts = D * 120;
    while (attempts < maxAttempts) {
      attempts++;
      var ang = rng() * Math.PI * 2;
      var rr = Math.sqrt(rng()) * R;
      var x = cx + Math.cos(ang) * rr, y = cy + Math.sin(ang) * rr;
      var rad = rMin + rng() * (rMax - rMin);
      if (Math.hypot(x - cx, y - cy) + rad > R) continue;
      var gx = Math.floor(x / cell), gy = Math.floor(y / cell), ok = true;
      for (var dx = -1; dx <= 1 && ok; dx++) for (var dy = -1; dy <= 1 && ok; dy++) {
        var bucket = grid[key(gx + dx, gy + dy)];
        if (!bucket) continue;
        for (var b = 0; b < bucket.length; b++) {
          var o = bucket[b];
          if (Math.hypot(x - o.x, y - o.y) < rad + o.r + gap) { ok = false; break; }
        }
      }
      if (!ok) continue;
      var dot = { x: x, y: y, r: rad };
      dots.push(dot);
      var kk = key(gx, gy); (grid[kk] || (grid[kk] = [])).push(dot);
    }
    return dots;
  }

  /* ===================================================================== */
  /* SECTION 6 — Render a plate                                             */
  /* ===================================================================== */
  /* The figure boundary exists ONLY as dot colour membership — no outline,  */
  /* no anti-aliased glyph edge. Per-dot OKLCH-L jitter (±JL) is drawn from   */
  /* the SAME distribution for figure and ground, so the two regions' bright- */
  /* ness statistics overlap completely and no luminance cue survives.        */
  var JITTER_L = 0.10, JITTER_C = 0.012, JITTER_H = 3;
  var PLATE_BG = 'rgb(236,233,225)';   // neutral cream, like real plates

  function jitterColor(baseRgb, rng, lShift) {
    var o = CVD.rgbToOklch(baseRgb[0], baseRgb[1], baseRgb[2]);
    var L = clamp(o[0] + (rng() * 2 - 1) * JITTER_L + (lShift || 0), 0.18, 0.97);
    var C = Math.max(0.02, o[1] + (rng() * 2 - 1) * JITTER_C);
    var h = o[2] + (rng() * 2 - 1) * JITTER_H;
    return CVD.oklchToRgb(L, C, h);
  }

  /**
   * `opts.reveal` (0..1) lifts the figure dots and sinks the ground dots in
   * LIGHTNESS. That is the one cue every kind of colour vision deficiency keeps
   * intact, so it shows a person who could not read the plate exactly where the
   * figure was — the thing they cannot get from a colour simulation, which for
   * their own type looks identical to the original. `opts.dots` replays the
   * exact dot layout from the sitting, so the revealed plate is the same plate.
   */
  function renderPlate(canvas, opts) {
    var D = canvas.width;
    var rng = opts.rng || Math.random;
    var fg = opts.fg, bg = opts.bg;
    var reveal = opts.reveal || 0;
    var mask = buildMask(opts.figure != null ? opts.figure : opts.digit, D);
    var dots = opts.dots || packDots(D, rng);
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, D, D);
    // circular cream backdrop
    ctx.save();
    ctx.beginPath(); ctx.arc(D / 2, D / 2, D / 2 - 1, 0, Math.PI * 2); ctx.clip();
    ctx.fillStyle = PLATE_BG; ctx.fillRect(0, 0, D, D);
    var i;
    for (i = 0; i < dots.length; i++) {
      var d = dots[i];
      var isFig = maskHit(mask, d.x, d.y);
      d.isFig = isFig;
      var col = jitterColor(isFig ? fg : bg, rng, reveal ? (isFig ? reveal * 0.26 : -reveal * 0.12) : 0);
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgb(' + col[0] + ',' + col[1] + ',' + col[2] + ')';
      ctx.fill();
    }
    ctx.restore();
    return { dots: dots, mask: mask };
  }

  /* ===================================================================== */
  /* SECTION 7 — Self-validation (region MEANS, chroma-only)                */
  /* ===================================================================== */
  /* Critical correctness point from the adversarial review: the per-dot     */
  /* luminance jitter makes per-PIXEL ΔE huge by design, so a per-pixel check */
  /* would reject every valid plate. We instead average the simulated a*,b*   */
  /* over the figure dots vs the ground dots and compare REGION MEANS:        */
  /*   • normal vision  → region-mean ΔE2000 must be LARGE (figure visible)   */
  /*   • target type    → chroma-plane separation must be SMALL (collapsed)   */
  /*   • luminance leak  → Rec.709 Michelson contrast of region means ≈ 0     */
  function validatePlate(render, fg, bg, type) {
    var dots = render.dots;
    function meanSimAB(simType, sev) {
      var fa = 0, fb = 0, fn = 0, ga = 0, gb = 0, gn = 0;
      for (var i = 0; i < dots.length; i++) {
        var base = dots[i].isFig ? fg : bg;
        var lab;
        if (simType) {
          var s = CVD.simulate(base[0], base[1], base[2], simType, sev);
          lab = CVD.rgbToLab(s[0], s[1], s[2]);
        } else {
          lab = CVD.rgbToLab(base[0], base[1], base[2]);
        }
        if (dots[i].isFig) { fa += lab[1]; fb += lab[2]; fn++; }
        else { ga += lab[1]; gb += lab[2]; gn++; }
      }
      if (!fn || !gn) return null;
      return { fa: fa / fn, fb: fb / fn, ga: ga / gn, gb: gb / gn };
    }
    // NOTE: this validates the base colours' separation in the relevant spaces.
    // (Per-dot jitter is symmetric and cancels in the mean, so base-colour means
    //  equal rendered-region means; we use base colours for determinism.)
    var nrm = meanSimAB(null);
    var sim = meanSimAB(type, 1.0);
    var normalSep = CVD.deltaE2000(
      CVD.rgbToLab(fg[0], fg[1], fg[2]), CVD.rgbToLab(bg[0], bg[1], bg[2]));
    var chromaSep = sim ? Math.hypot(sim.fa - sim.ga, sim.fb - sim.gb) : 99;
    // luminance leak: a dichromat keeps full luminance vision
    var lf = CVD.relativeLuminance(fg[0], fg[1], fg[2]);
    var lg = CVD.relativeLuminance(bg[0], bg[1], bg[2]);
    var michelson = Math.abs(lf - lg) / (lf + lg + 1e-6);
    return {
      normalSep: normalSep,           // want >= NORMAL_MIN
      chromaSep: chromaSep,           // want small for target type (<~4)
      luminanceMichelson: michelson,  // want near 0 (figure not readable by brightness)
      pass: normalSep >= NORMAL_MIN && chromaSep <= 5 && michelson <= 0.06,
      nrm: nrm, sim: sim
    };
  }

  /* ===================================================================== */
  /* SECTION 8 — Farnsworth D-15 (equal L / equal C caps + V&KS scoring)    */
  /* ===================================================================== */
  /* 16 caps (pilot + 1..15) at constant OKLCH lightness & chroma around the */
  /* hue circle — equal lightness means the subject cannot sort by brightness*/
  /* (a classic D-15 cheat). Scoring is done in CIE 1976 u'v' computed from   */
  /* each cap's actual rendered sRGB, so screen and scorer always agree.      */
  var D15_L = 0.66, D15_C = 0.105;
  var D15_HUES = (function () {
    // pilot ~ blue, then march around the circle the way the physical D-15 does
    // (blue → blue-green → green → yellow → orange → red → purple → back to blue).
    var hues = [];
    var start = 264;            // bluish pilot
    for (var i = 0; i < 16; i++) hues.push((start - i * (360 / 16) + 360) % 360);
    return hues;
  })();

  function buildD15Caps() {
    var caps = [];
    for (var i = 0; i < D15_HUES.length; i++) {
      var rgb = CVD.oklchToRgb(D15_L, D15_C, D15_HUES[i]);
      caps.push({ id: i, hex: CVD.rgbToHex(rgb[0], rgb[1], rgb[2]), rgb: rgb, uv: rgbToUv(rgb) });
    }
    return caps;   // caps[0] = pilot
  }

  function rgbToUv(rgb) {
    // sRGB → XYZ (D65) → u'v'
    var lr = srgbToLinear(rgb[0]), lg = srgbToLinear(rgb[1]), lb = srgbToLinear(rgb[2]);
    var X = 0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb;
    var Y = 0.2126729 * lr + 0.7151522 * lg + 0.0721750 * lb;
    var Z = 0.0193339 * lr + 0.1191920 * lg + 0.9503041 * lb;
    var den = X + 15 * Y + 3 * Z;
    if (den <= 0) return [0, 0];
    return [4 * X / den, 9 * Y / den];
  }

  // Vingrys & King-Smith (1988) moment analysis. `order` = array of cap ids in
  // the subject's arrangement (pilot first). u'v' scaled ×100 (the conventional
  // unit that yields a perfect-arrangement TES ≈ 10–11).
  function vksMoments(orderUv) {
    var Sxx = 0, Syy = 0, Sxy = 0;
    for (var k = 0; k < orderUv.length - 1; k++) {
      var dx = (orderUv[k + 1][0] - orderUv[k][0]) * 100;
      var dy = (orderUv[k + 1][1] - orderUv[k][1]) * 100;
      Sxx += dx * dx; Syy += dy * dy; Sxy += dx * dy;
    }
    var avg = (Sxx + Syy) / 2, diff = (Sxx - Syy) / 2;
    var Rr = Math.sqrt(diff * diff + Sxy * Sxy);
    var Imaj = avg + Rr, Imin = avg - Rr;
    var angle = 0.5 * Math.atan2(2 * Sxy, Sxx - Syy) * 180 / Math.PI; // (-90,90]
    return {
      majorRadius: Math.sqrt(Math.max(0, Imaj)),
      minorRadius: Math.sqrt(Math.max(0, Imin)),
      angle: angle
    };
  }

  function scoreD15(order, caps) {
    caps = caps || buildD15Caps();
    var byId = {}; caps.forEach(function (c) { byId[c.id] = c; });
    var orderUv = order.map(function (id) { return byId[id].uv; });
    var perfectOrder = []; for (var i = 0; i < caps.length; i++) perfectOrder.push(i);
    var perfectUv = perfectOrder.map(function (id) { return byId[id].uv; });

    var m = vksMoments(orderUv);
    var p = vksMoments(perfectUv);

    var TES = m.majorRadius + m.minorRadius;                 // SUM, not RMS
    var Cindex = p.majorRadius > 0 ? m.majorRadius / p.majorRadius : 1;
    var Sindex = m.minorRadius > 0 ? m.majorRadius / m.minorRadius : 99;

    // Confusion-angle classification. The boundaries are calibrated to THIS
    // cap set's own confusion geometry (verified empirically with synthetic
    // protan/deutan/tritan exemplar arrangements driven by CVD.simulate, which
    // land at ≈ +1.5° / -9° / -56° in our u'v'). protan vs deutan is genuinely
    // close (near-parallel red-green lines) — combineVerdict lowers confidence
    // when the modules disagree, rather than over-committing to a label.
    var ang = m.angle;
    var type = 'normal', confident = false;
    if (Cindex >= 1.5 && Sindex >= 1.6) {
      confident = true;
      if (ang <= -30) type = 'tritan';
      else if (ang > -4) type = 'protan';
      else type = 'deutan';
    }
    // crossings on the standard circle (pass/fail)
    var crossings = countCrossings(order, caps.length);
    return {
      TES: round2(TES), Cindex: round2(Cindex), Sindex: round2(Sindex),
      angle: round2(ang), crossings: crossings,
      type: type, confident: confident,
      perfectMajor: round2(p.majorRadius), perfectMinor: round2(p.minorRadius),
      defect: Cindex >= 1.5 || crossings >= 2
    };
  }

  // A "crossing" = the subject placed two caps adjacent that are far apart on the
  // reference hue circle (a chord across the diagram). Count major transpositions.
  function countCrossings(order, n) {
    var crossings = 0;
    for (var k = 0; k < order.length - 1; k++) {
      var a = order[k], b = order[k + 1];
      var circDist = Math.min(Math.abs(a - b), n - Math.abs(a - b));
      if (circDist >= 3) crossings++;   // non-adjacent on the circle
    }
    return crossings;
  }

  /* ===================================================================== */
  /* SECTION 9 — Trivector discrimination (Cambridge-Colour-Test style)     */
  /* ===================================================================== */
  /* Stimulus colours come from u'v' → sRGB at a fixed luminance, displaced  */
  /* along each type's confusion line. window.CVD is used ONLY for sanity    */
  /* checks / the "what a deficient observer sees" preview — never to build   */
  /* the stimulus (it is a forward appearance model, not a stimulus source).  */
  var TRI_BG_UV = [0.1977, 0.4689];   // CCT near-D65/EE neutral
  var COPUNCTAL = { protan: [0.678, 0.501], deutan: [-1.217, 0.782], tritan: [0.257, 0.0] };
  var TRI_DIR = (function () {
    var d = {};
    RG_TYPES.forEach(function (t) {
      var dx = COPUNCTAL[t][0] - TRI_BG_UV[0], dy = COPUNCTAL[t][1] - TRI_BG_UV[1];
      var n = Math.hypot(dx, dy);
      d[t] = [dx / n, dy / n];
    });
    return d;
  })();

  // u'v' (+ luminance Y in 0..1 linear) → sRGB 0..255, with in-gamut flag.
  function uvYToRgb(u, v, Y) {
    var den = 6 * u - 16 * v + 12;
    var x = 9 * u / den, y = 4 * v / den;
    var X = (x / y) * Y, Z = ((1 - x - y) / y) * Y;
    var lr = 3.2406 * X - 1.5372 * Y - 0.4986 * Z;
    var lg = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
    var lb = 0.0557 * X - 0.2040 * Y + 1.0570 * Z;
    var inGamut = lr >= -1e-4 && lr <= 1.0001 && lg >= -1e-4 && lg <= 1.0001 && lb >= -1e-4 && lb <= 1.0001;
    var r = Math.round(clamp(linearToSrgb01(clamp(lr, 0, 1)), 0, 1) * 255);
    var g = Math.round(clamp(linearToSrgb01(clamp(lg, 0, 1)), 0, 1) * 255);
    var b = Math.round(clamp(linearToSrgb01(clamp(lb, 0, 1)), 0, 1) * 255);
    return { rgb: [r, g, b], inGamut: inGamut };
  }

  // Target chromaticity at displacement r (in 1e-4 u'v' units) along a line.
  function targetUv(type, r, polarity) {
    var dir = TRI_DIR[type], s = (polarity === undefined ? 1 : polarity) * r * 1e-4;
    return [TRI_BG_UV[0] + dir[0] * s, TRI_BG_UV[1] + dir[1] * s];
  }

  // Largest in-gamut displacement on a line at luminance Y (deutan caps ~640).
  function maxInGamutR(type, Y, polarity) {
    var lo = 0, hi = 3000;
    for (var it = 0; it < 24; it++) {
      var mid = (lo + hi) / 2;
      var uv = targetUv(type, mid, polarity);
      if (uvYToRgb(uv[0], uv[1], Y).inGamut) lo = mid; else hi = mid;
    }
    return lo;
  }

  // Relative-luminance noise set (NOT cd/m² — only the spread matters on screen).
  var TRI_LUM_SET = [0.30, 0.36, 0.42, 0.48, 0.54, 0.60];

  // Background sRGB at a given luminance level.
  function triBgRgb(Y) { return uvYToRgb(TRI_BG_UV[0], TRI_BG_UV[1], Y).rgb; }
  // Target sRGB at displacement r and luminance Y (clamped to gamut).
  function triTargetRgb(type, r, Y, polarity) {
    var uv = targetUv(type, r, polarity); return uvYToRgb(uv[0], uv[1], Y).rgb;
  }

  // Normal-observer reference thresholds (1e-4 u'v'), soft & lab-dependent.
  var TRI_NORMAL = { protan: 100, deutan: 100, tritan: 150 };
  var TRI_CUT = { protan: 150, deutan: 150, tritan: 250 };

  function classifyTrivector(tri) {
    // tri = {protan:r, deutan:r, tritan:r}. Returns dominant axis if elevated.
    var elevated = [];
    RG_TYPES.forEach(function (t) { if (tri[t] != null && tri[t] > TRI_CUT[t]) elevated.push(t); });
    if (!elevated.length) return { type: 'normal', detail: tri };
    elevated.sort(function (a, b) { return (tri[b] / TRI_CUT[b]) - (tri[a] / TRI_CUT[a]); });
    return { type: elevated[0], detail: tri, elevated: elevated };
  }

  /* ===================================================================== */
  /* SECTION 10 — Combine the three modules into one screening verdict      */
  /* ===================================================================== */
  /* Confidence DOWNGRADES on disagreement between modules — the honest way   */
  /* to express that an uncalibrated-screen screening is not a diagnosis.     */
  function combineVerdict(ish, d15, tri) {
    // ish = {protan, deutan, tritan, redgreen, missedDemo, total}
    // d15 = scoreD15 result (or null), tri = classifyTrivector result (or null)
    var votes = { protan: 0, deutan: 0, tritan: 0, normal: 0 };
    var weight = { ish: 1.0, d15: 1.0, tri: 0.8 };

    var ishType = 'normal';
    if (ish) {
      if (ish.tritan > ish.redgreen && ish.tritan >= 2) ishType = 'tritan';
      else if (ish.redgreen >= 2) ishType = ish.protan >= ish.deutan ? 'protan' : 'deutan';
    }
    votes[ishType] += weight.ish;
    if (d15 && d15.confident && d15.type !== 'normal' && d15.type !== 'indeterminate') votes[d15.type] += weight.d15;
    else if (d15 && !d15.defect) votes.normal += weight.d15;
    if (tri && tri.type !== 'normal') votes[tri.type] += weight.tri;
    else if (tri) votes.normal += weight.tri;

    var best = 'normal', bestV = votes.normal;
    ['protan', 'deutan', 'tritan'].forEach(function (t) { if (votes[t] > bestV) { bestV = votes[t]; best = t; } });

    // severity from D-15 C-index primarily, else Ishihara miss count
    var severity = 'none';
    if (best !== 'normal') {
      if (d15 && d15.confident) {
        if (d15.Cindex >= 3) severity = 'strong';
        else if (d15.Cindex >= 1.8) severity = 'moderate';
        else severity = 'mild';
      } else if (ish) {
        var miss = best === 'tritan' ? ish.tritan : ish.redgreen;
        severity = miss >= 4 ? 'strong' : miss >= 2 ? 'moderate' : 'mild';
      } else severity = 'mild';
    }

    // confidence: how many modules agreed
    var agree = 0, modules = 0;
    if (ish) { modules++; if (ishType === best) agree++; }
    if (d15 && d15.confident) { modules++; if (d15.type === best) agree++; }
    if (tri) { modules++; if (tri.type === best) agree++; }
    var confidence = modules === 0 ? 'low' : (agree >= modules ? 'high' : agree >= 1 ? 'moderate' : 'low');
    if (best === 'normal') confidence = (ish && ish.missedDemo) ? 'low' : confidence;

    return { type: best, severity: severity, confidence: confidence, votes: votes };
  }


  /* ===================================================================== */
  /* SECTION 11 — the eye: cone contrasts → cardinal channels → d′          */
  /* ===================================================================== */
  /* Everything above this line designs stimuli with Brettel, which is a     */
  /* rendering model: it answers "what does this look like to them". Scoring */
  /* needs a different question — "can they TELL these two apart" — and that */
  /* is a question about cone signals and channel thresholds, not about      */
  /* appearance. Using Brettel for both is how the tool ended up with a      */
  /* trivector axis that leaks 0.29 for deutan and 0.62 for tritan (measured */
  /* 2026-09-05): the axes were drawn from classical copunctal points, the   */
  /* eye was Brettel, and nothing in the codebase could see the disagreement.*/
  /*                                                                        */
  /* Channel thresholds are in cone-contrast units. Red-green is an order of */
  /* magnitude more sensitive than blue-yellow — which is exactly why an     */
  /* isoluminant red-green plate is readable at all.                        */
  var CONE_SIGMA = { rg: 0.0042, yv: 0.048, lum: 0.021 };

  /* Anomalous / absent pigment: the affected cone's signal is pulled toward */
  /* its neighbour. severity 1 is dichromacy exactly, 0 leaves a trichromat  */
  /* untouched, and everything between is anomalous trichromacy. Protan and  */
  /* deutan end up with DIFFERENT residual luminance channels (M vs L), which */
  /* is why their confusion lines differ — a "just drop red-green" shortcut   */
  /* would have thrown that away.                                            */
  var _coneMemo = {};
  function conesSeen(rgb, type, sev) {
    var key = type + '|' + sev + '|' + rgb[0] + ',' + rgb[1] + ',' + rgb[2];
    var hit = _coneMemo[key];
    if (hit) return hit;
    var lms = CVD.rgbToLms(rgb[0], rgb[1], rgb[2]);
    var L = lms[0], M = lms[1], S = lms[2];
    var s = (!type || type === 'normal') ? 0 : sev;
    if (type === 'protan') L = (1 - s) * L + s * M;
    else if (type === 'deutan') M = (1 - s) * M + s * L;
    else if (type === 'tritan') S = (1 - s) * S + s * 0.5 * (L + M);
    var out = [L, M, S];
    _coneMemo[key] = out;
    return out;
  }

  /** Discrimination index between two colours seen against an adapting field. */
  function dprimeParts(a, b, adapt, type, sev) {
    var ca = conesSeen(a, type, sev), cb = conesSeen(b, type, sev), w = conesSeen(adapt, type, sev);
    var dL = (ca[0] - cb[0]) / Math.max(1e-6, w[0]);
    var dM = (ca[1] - cb[1]) / Math.max(1e-6, w[1]);
    var dS = (ca[2] - cb[2]) / Math.max(1e-6, w[2]);
    var rg = (dL - dM) / CONE_SIGMA.rg;
    var yv = (dS - 0.5 * (dL + dM)) / CONE_SIGMA.yv;
    var lum = (0.7 * dL + 0.3 * dM) / CONE_SIGMA.lum;
    return { rg: rg, yv: yv, lum: lum, d: Math.sqrt(rg * rg + yv * yv + lum * lum), chroma: Math.sqrt(rg * rg + yv * yv) };
  }
  var PLATE_ADAPT = [236, 233, 225];   // the cream disc
  var D15_ADAPT = [120, 120, 120];

  /* ===================================================================== */
  /* SECTION 12 — trivector axes: polarity and measured dynamic range       */
  /* ===================================================================== */
  /* A confusion LINE is undirected but Brettel — and the sRGB gamut — are   */
  /* not: the two directions away from the neutral point reach very          */
  /* different distances before leaving the gamut. The shipped code always   */
  /* used polarity +1, which gave protan 1462 units of range and deutan 643. */
  /* `classifyTrivector` then ranked axes by threshold/cut, so ANY responder */
  /* pushed to the ceiling was ranked protan first — by construction, not by */
  /* anything about the person. Choosing each axis's polarity for reach      */
  /* equalises the three axes (1462 / 1370 / 1266) and removes that bias at  */
  /* its source. Leak is verified with the cone model, not assumed.          */
  var TRI_AXES = (function () {
    var Ylo = TRI_LUM_SET[0], out = {};
    for (var i = 0; i < RG_TYPES.length; i++) {
      var axis = RG_TYPES[i], best = null;
      for (var p = 0; p < 2; p++) {
        var pol = p ? -1 : 1;
        var maxR = maxInGamutR(axis, Ylo, pol);
        if (maxR < 100) continue;
        var rMid = maxR * 0.5;
        var uv = targetUv(axis, rMid, pol);
        var Y = Ylo;
        for (var k = 0; k < TRI_LUM_SET.length; k++) { if (uvYToRgb(uv[0], uv[1], TRI_LUM_SET[k]).inGamut) { Y = TRI_LUM_SET[k]; break; } }
        var bg = triBgRgb(Y), tg = triTargetRgb(axis, rMid, Y, pol);
        var seenByNormal = dprimeParts(bg, tg, bg, 'normal', 0).chroma;
        var seenByType = dprimeParts(bg, tg, bg, axis, 1).chroma;
        var leak = seenByNormal > 0 ? seenByType / seenByNormal : 1;
        if (leak > 0.08) continue;            // not a usable confusion line
        if (!best || maxR > best.maxR) best = { polarity: pol, maxR: maxR, leak: leak };
      }
      if (!best) best = { polarity: 1, maxR: maxInGamutR(axis, Ylo, 1), leak: null };
      out[axis] = best;
    }
    return out;
  })();

  function triChooseY(axis, r, pol) {
    var uv = targetUv(axis, r, pol);
    for (var i = 0; i < TRI_LUM_SET.length; i++) if (uvYToRgb(uv[0], uv[1], TRI_LUM_SET[i]).inGamut) return TRI_LUM_SET[i];
    return TRI_LUM_SET[0];
  }

  /** Chromatic d′ of the trivector target at displacement r, for one observer. */
  function triDprime(axis, r, type, sev) {
    var ax = TRI_AXES[axis], Y = triChooseY(axis, r, ax.polarity);
    var bg = triBgRgb(Y), tg = triTargetRgb(axis, r, Y, ax.polarity);
    return dprimeParts(bg, tg, bg, type, sev).chroma;
  }

  /** Displacement at which this observer reaches the staircase's target d′. */
  var TRI_DTHR = 5.0;          // assumed population-mean sensitivity, in d′
  var _thrMemo = {};
  function triPredictedThreshold(axis, type, sev) {
    var key = axis + '|' + type + '|' + sev;
    if (_thrMemo[key] != null) return _thrMemo[key];
    var ax = TRI_AXES[axis], lo = 5, hi = ax.maxR, mid = 0;
    if (triDprime(axis, hi, type, sev) < TRI_DTHR) { _thrMemo[key] = Infinity; return Infinity; }
    for (var i = 0; i < 30; i++) {
      mid = (lo + hi) / 2;
      if (triDprime(axis, mid, type, sev) < TRI_DTHR) lo = mid; else hi = mid;
    }
    _thrMemo[key] = mid;
    return mid;
  }

  /* ===================================================================== */
  /* SECTION 13 — ZEST: an adaptive procedure that actually converges       */
  /* ===================================================================== */
  /* The shipped 2-down-1-up staircase could not reach a threshold inside its */
  /* budget: from a start 15-30x above a normal observer's threshold it needs */
  /* 28-40 correct trials to descend and had 24, so the number it reported    */
  /* tracked the STARTING POINT, not the person (measured: halving the start  */
  /* halves the reported "threshold"). ZEST places every trial at the current */
  /* posterior mean over log-threshold and returns the posterior, so it       */
  /* converges in ~14 trials AND says how sure it is — which the verdict then */
  /* uses instead of pretending a number is exact.                            */
  var ZEST_DEFAULTS = {
    trials: 15,
    catchEvery: 4,          // a trial nobody with that axis intact can miss
    beta: 2.4,              // psychometric slope in r-units
    lapse: 0.06,
    guess: 0.25,            // 4AFC
    gridSteps: 61,
    sdStop: 0.10,           // stop early once the posterior is this tight (log10)
    catchLumBoost: 1.9,     // how much brighter the attention ring is than the noise
  };

  function makeZest(axis, opts) {
    var o = {}, k;
    for (k in ZEST_DEFAULTS) o[k] = ZEST_DEFAULTS[k];
    if (opts) for (k in opts) o[k] = opts[k];
    var ax = TRI_AXES[axis];
    var loLog = Math.log10(15), hiLog = Math.log10(ax.maxR * 1.6);
    var grid = [], prior = [];
    // Prior: most people are normal (threshold near 70), but leave real mass all
    // the way to the ceiling so a dichromat is not fought by the prior.
    for (var i = 0; i < o.gridSteps; i++) {
      var lg = loLog + (hiLog - loLog) * i / (o.gridSteps - 1);
      grid.push(lg);
      var z = (lg - Math.log10(75)) / 0.30;
      prior.push(0.55 * Math.exp(-0.5 * z * z) + 0.45);
    }
    var post = prior.slice(), n = 0;
    normalise();
    var trials = 0, catchDone = 0, catchFails = 0, history = [];

    function normalise() { var s = 0, i; for (i = 0; i < post.length; i++) s += post[i]; for (i = 0; i < post.length; i++) post[i] /= s; }
    function mean() { var m = 0, i; for (i = 0; i < grid.length; i++) m += grid[i] * post[i]; return m; }
    function sd() { var m = mean(), v = 0, i; for (i = 0; i < grid.length; i++) v += post[i] * (grid[i] - m) * (grid[i] - m); return Math.sqrt(v); }
    function pCorrect(r, thr) { return o.guess + (1 - o.guess - o.lapse) * (1 - Math.exp(-Math.pow(r / thr, o.beta))); }

    return {
      axis: axis,
      /**
       * The displacement to show next. `isCatch` marks an ATTENTION probe, and
       * it is deliberately a LUMINANCE target, not a chromatic one: a probe
       * built from the axis under test is failed honestly by the very people
       * the test exists to find — a deuteranope misses ~68 % of chromatic
       * catch trials on the deutan axis — so it would have branded every
       * dichromat "inattentive". Luminance is intact in every kind of colour
       * vision deficiency, so a luminance ring asks only "are you looking".
       */
      next: function () {
        var isCatch = o.catchEvery > 0 && trials > 0 && trials % o.catchEvery === 0 && catchDone < Math.floor(o.trials / o.catchEvery);
        if (isCatch) {
          return { r: 0, isCatch: true, lumCatch: true, polarity: ax.polarity, Y: TRI_LUM_SET[0], lumBoost: o.catchLumBoost };
        }
        var r = Math.min(ax.maxR * 0.95, Math.max(15, Math.pow(10, mean())));
        return { r: r, isCatch: false, polarity: ax.polarity, Y: triChooseY(axis, r, ax.polarity) };
      },
      respond: function (trial, correct) {
        trials++;
        history.push({ r: trial.r, correct: correct, isCatch: !!trial.isCatch });
        if (trial.isCatch) { catchDone++; if (!correct) catchFails++; return; }
        for (var i = 0; i < grid.length; i++) {
          var p = pCorrect(trial.r, Math.pow(10, grid[i]));
          post[i] *= correct ? p : (1 - p);
        }
        normalise();
      },
      done: function () { return trials >= o.trials || (trials >= 8 && sd() < o.sdStop && catchDone > 0); },
      result: function () {
        var thr = Math.pow(10, mean());
        return {
          axis: axis, thr: Math.round(thr), logThr: mean(), logSd: sd(),
          ceiling: thr >= ax.maxR * 0.8, maxR: Math.round(ax.maxR),
          trials: trials, catchDone: catchDone, catchFails: catchFails,
          reliable: catchFails <= 1,
          history: history,
        };
      },
    };
  }

  /* ===================================================================== */
  /* SECTION 14 — one verdict from all the evidence                         */
  /* ===================================================================== */
  /* The shipped verdict was a weighted vote of three hard labels, and every  */
  /* one of the three could produce a label out of noise. This replaces it    */
  /* with a posterior over observers: each hypothesis predicts what the three  */
  /* measurements should look like, and we ask which hypothesis explains what  */
  /* we actually saw.                                                          */
  /*                                                                          */
  /* The decisive part is the NUISANCE PARAMETERS. A person can be sloppy, or  */
  /* on a dim screen, or tapping through — that raises every threshold and      */
  /* scatters every arrangement, uniformly. Marginalising over "how sloppy was  */
  /* this person" means a uniform elevation is explained WITHOUT invoking a     */
  /* deficiency, while a SELECTIVE elevation on one axis still is. That is why  */
  /* a random responder used to come out protan and now comes out inconclusive. */

  var SEVERITY_GRID = [0.35, 0.5, 0.65, 0.8, 1.0];
  /* Prior. Relative rates inside the deficient group are epidemiology
     (deutan ≈ 2/3 of deficiency, protan ≈ 1/4, tritan rare and mostly acquired).
     P(normal) is a property of who visits a colour-vision test, not of the
     street, so it is deliberately weak — the evidence should move it easily. */
  var VERDICT_PRIOR = { normal: 0.55, protan: 0.115, deutan: 0.29, tritan: 0.045 };
  var SEVERITY_PRIOR = { 0.35: 0.30, 0.5: 0.26, 0.65: 0.18, 0.8: 0.12, 1.0: 0.14 };

  function buildHypotheses() {
    var hs = [{ key: 'normal', type: 'normal', severity: 0, logPrior: Math.log(VERDICT_PRIOR.normal) }];
    for (var t = 0; t < RG_TYPES.length; t++) {
      for (var s = 0; s < SEVERITY_GRID.length; s++) {
        var ty = RG_TYPES[t], sv = SEVERITY_GRID[s];
        hs.push({ key: ty + '@' + sv, type: ty, severity: sv, logPrior: Math.log(VERDICT_PRIOR[ty] * SEVERITY_PRIOR[sv]) });
      }
    }
    return hs;
  }
  var HYPOTHESES = buildHypotheses();

  function logSumExp(arr) {
    var m = -Infinity, i;
    for (i = 0; i < arr.length; i++) if (arr[i] > m) m = arr[i];
    if (m === -Infinity) return -Infinity;
    var s = 0;
    for (i = 0; i < arr.length; i++) s += Math.exp(arr[i] - m);
    return m + Math.log(s);
  }
  function normPdfLog(x, mu, sigma) { var z = (x - mu) / sigma; return -0.5 * z * z - Math.log(sigma) - 0.9189385332; }
  /** P(X >= k) for X ~ Binomial(n, p). */
  function binomTailAtLeast(k, n, p) {
    if (k <= 0) return 1;
    var logFact = function (x) { var r = 0; for (var i = 2; i <= x; i++) r += Math.log(i); return r; };
    var s = 0;
    for (var i = k; i <= n; i++) {
      s += Math.exp(logFact(n) - logFact(i) - logFact(n - i) + i * Math.log(p) + (n - i) * Math.log(1 - p));
    }
    return Math.min(1, s);
  }
  function normCdf(z) {  // Abramowitz-Stegun
    var t = 1 / (1 + 0.2316419 * Math.abs(z));
    var d = 0.3989422804 * Math.exp(-z * z / 2);
    var p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return z > 0 ? 1 - p : p;
  }

  /* — the person-level sensitivity nuisance, shared by all three modules —   */
  /* Not everybody with normal colour vision has AVERAGE colour vision. Age,   */
  /* a dim or badly-profiled screen, a phone at 30 % brightness in daylight:   */
  /* all of them lower chromatic sensitivity UNIFORMLY. A scorer that assumes  */
  /* one fixed sensitivity reads that uniform loss as a deficiency — measured  */
  /* on the bench, a "duller eyes" population sent false positives to 72 %,    */
  /* WORSE than the old vote it was replacing. The signature of a real         */
  /* red-green deficiency is not "everything is harder", it is "this ONE axis  */
  /* collapsed while the others did not", so the model has to be able to say   */
  /* "this person is simply less sensitive" out loud. `g` is that sentence:    */
  /* one gain on d′, marginalised over, shared by plates, arrangement and      */
  /* thresholds so it cannot be spent twice.                                   */
  var SENS_GRID = [0.30, 0.45, 0.60, 0.80, 1.00, 1.30, 1.70];
  var SENS_PRIOR = [0.04, 0.07, 0.13, 0.23, 0.27, 0.18, 0.08];
  /* Below this the person is discriminating colour so much worse than average,
     ON EVERY AXIS INCLUDING THE ONE THEY SHOULD HAVE, that the sitting is not
     measuring a type any more. The honest answer is the one the tool's own
     setup hint already worries about — night mode, a blue-light filter, a dim
     screen in daylight — not a diagnosis. Measured on the bench: without this,
     an observer half as sensitive as average was called red-green deficient
     85 % of the time (the shipped vote managed 63 %, so the rebuild was
     WORSE there); with it, that case is told the sitting could not be scored. */
  var SENS_TOO_LOW = 0.62;

  /* — evidence 1: the plates — */
  var PLATE_D50 = 6.5, PLATE_BETA = 2.2;
  var PLATE_LAPSE_GRID = [0.02, 0.10, 0.25];
  var PLATE_LAPSE_PRIOR = [0.55, 0.30, 0.15];

  function plateLogLik(answers, h, g) {
    var perLapse = [];
    for (var gi = 0; gi < PLATE_LAPSE_GRID.length; gi++) {
      var lapse = PLATE_LAPSE_GRID[gi], ll = 0;
      for (var i = 0; i < answers.length; i++) {
        var a = answers[i];
        if (!a.fg || !a.bg) continue;
        var d = g * dprimeParts(a.fg, a.bg, PLATE_ADAPT, h.type, h.severity).chroma;
        /* The guess rate travels WITH the answer. Naming one of six shapes is
           right one time in six by luck; typing a two-digit number is not. A
           fixed rate would read those lucky sixths as evidence of reading. */
        var guess = (a.guess != null) ? a.guess : 0.02;
        var p = guess + (1 - guess - lapse) * (1 - Math.exp(-Math.pow(d / PLATE_D50, PLATE_BETA)));
        p = Math.min(0.995, Math.max(0.005, p));
        ll += a.correct ? Math.log(p) : Math.log(1 - p);
      }
      perLapse.push(Math.log(PLATE_LAPSE_PRIOR[gi]) + ll);
    }
    return { total: logSumExp(perLapse), terms: perLapse, grid: PLATE_LAPSE_GRID };
  }

  /* — evidence 2: the D-15 arrangement — */
  /* Likelihood of the WHOLE arrangement under a "pick the nearest remaining
     cap, with noise" observer. This replaces reading two moments off the path:
     a single slipped cap used to make the moment analysis "confident" with an
     essentially random confusion angle, and 48 % of those random angles landed
     in the protan bucket because that bucket spans 94° of the circle. */
  var D15_TAU_GRID = [2.0, 4.0, 9.0, 20.0];
  var D15_TAU_PRIOR = [0.32, 0.34, 0.22, 0.12];
  /* One misplaced cap costs TWO picks in this likelihood (the cap that went to
     the wrong slot, and the one displaced by it), and real arrangement errors
     come in runs rather than independently. Treating each pick as independent
     evidence therefore over-states how much a single slip tells us — an
     attentive normal with two swaps came out at 8 % "normal" on the bench.
     Tempering is the standard correction for a likelihood known to be
     over-precise, and 0.45 is the knee of a measured sweep: below it sensitivity
     stops improving, above it false positives on attentive normal observers
     climb 0.6 % -> 3.7 % for 1.5 points of sensitivity. */
  var D15_TEMPER = 0.45;

  /* `memo` caches the pairwise cap distances for ONE hypothesis.
     dprimeParts depends on the two caps and on (h.type, h.severity) — not on
     tau, which only enters the softmax below, and not on g, which only scales
     the result. It was nevertheless being recomputed inside both loops:
     16 hypotheses x 7 gains x 4 taus x ~120 pairs = 53,760 calls per verdict,
     for 3,840 distinct values. That was three quarters of assess()'s cost, on a
     tool whose own budget line says three quarters of its traffic is mobile. */
  function d15LogLik(order, caps, h, g, memo) {
    if (!order || order.length < 3) return { total: 0, terms: [0], grid: [0] };
    var i;
    var mem = memo || {};
    var byId = mem.byId;
    if (!byId) {
      byId = mem.byId = {};
      for (i = 0; i < caps.length; i++) byId[caps[i].id] = caps[i];
    }
    var dcache = mem.d || (mem.d = {});
    var perTau = [];
    for (var gi = 0; gi < D15_TAU_GRID.length; gi++) {
      var tau = D15_TAU_GRID[gi], ll = 0;
      var remaining = [];
      for (i = 1; i < order.length; i++) remaining.push(order[i]);
      var cur = byId[order[0]];
      var pool = remaining.slice();
      for (var step = 0; step < remaining.length; step++) {
        var chosen = remaining[step];
        var ds = [], j, minD = Infinity;
        for (j = 0; j < pool.length; j++) {
          var key = cur.id + '\u0000' + pool[j];
          var base = dcache[key];
          if (base === undefined) {
            base = dprimeParts(cur.rgb, byId[pool[j]].rgb, D15_ADAPT, h.type, h.severity).d;
            dcache[key] = base;
          }
          var dd = g * base;
          ds.push(dd); if (dd < minD) minD = dd;
        }
        var tot = 0, pick = -1;
        for (j = 0; j < pool.length; j++) { tot += Math.exp(-(ds[j] - minD) / tau); if (pool[j] === chosen) pick = j; }
        if (pick < 0) break;
        ll += -(ds[pick] - minD) / tau - Math.log(tot);
        cur = byId[chosen];
        pool.splice(pick, 1);
      }
      perTau.push(Math.log(D15_TAU_PRIOR[gi]) + D15_TEMPER * ll);
    }
    return { total: logSumExp(perTau), terms: perTau, grid: D15_TAU_GRID };
  }

  /* — evidence 3: the trivector thresholds — */
  /* `g` above already carries person-level sensitivity, so μ here is only the
     residual: session-to-session state, adaptation, the fact that the three
     axes are measured one after another. Keeping it narrow stops the two terms
     from absorbing the same effect twice. */
  var TRI_SIGMA_WITHIN = 0.16, TRI_MU_SIGMA = 0.10;
  /* Ablation hook for the bench; not used by the page. */
  function setTriMuSigma(v) { var old = TRI_MU_SIGMA; if (v != null) TRI_MU_SIGMA = v; return old; }

  function triLogLik(tri, h, g) {
    if (!tri) return { total: 0, terms: [0], grid: [0] };
    var axes = [], i;
    for (i = 0; i < RG_TYPES.length; i++) if (tri[RG_TYPES[i]]) axes.push(RG_TYPES[i]);
    if (!axes.length) return { total: 0, terms: [0], grid: [0] };
    var logG = Math.log10(g);
    var terms = [], muGrid = [];
    for (var mi = -8; mi <= 8; mi++) {
      var mu = mi * 0.06;
      muGrid.push(mu);
      var ll = normPdfLog(mu, 0, TRI_MU_SIGMA);
      for (i = 0; i < axes.length; i++) {
        var axis = axes[i], meas = tri[axis];
        var pred = triPredictedThreshold(axis, h.type, h.severity);
        // a more sensitive observer reaches the criterion at a smaller displacement
        var predLog = (pred === Infinity ? Math.log10(TRI_AXES[axis].maxR * 2.2) : Math.log10(pred)) - logG;
        var sd = (meas.logSd == null ? 0.12 : meas.logSd);
        var sigma = Math.sqrt(TRI_SIGMA_WITHIN * TRI_SIGMA_WITHIN + sd * sd);
        if (meas.ceiling) {
          // censored: we only know the threshold is at least the ceiling
          var z = (predLog + mu - Math.log10(meas.thr)) / sigma;
          ll += Math.log(Math.max(1e-9, normCdf(z)));
        } else {
          ll += normPdfLog(Math.log10(meas.thr), predLog + mu, sigma);
        }
      }
      terms.push(ll);
    }
    return { total: logSumExp(terms) - Math.log(muGrid.length), terms: terms, grid: muGrid };
  }

  /**
   * The verdict.
   *
   * `m` = { answers:[{fg,bg,correct,kind,type}], order:[capId], caps:[],
   *         tri:{ protan:{thr,logSd,ceiling,catchDone,catchFails}, ... } }
   *
   * Returns a posterior over observers plus the honest labels a screening test
   * is entitled to use: a specific type, the "red-green" umbrella when protan
   * and deutan cannot be told apart on a screen, "normal", or "inconclusive"
   * when the sitting itself was not measurable.
   */
  function assess(m, opts) {
    opts = opts || {};
    var logs = [], i, gi, h, cells = [];
    for (i = 0; i < HYPOTHESES.length; i++) {
      h = HYPOTHESES[i];
      var perG = [], perGparts = [];
      /* One cache per hypothesis: the gain loop below reuses the same pairwise
         distances seven times over. */
      var d15Memo = {};
      for (gi = 0; gi < SENS_GRID.length; gi++) {
        var g = SENS_GRID[gi];
        var l = Math.log(SENS_PRIOR[gi]);
        var pl = (m.answers && m.answers.length) ? plateLogLik(m.answers, h, g) : null;
        var pd = (m.order && m.caps) ? d15LogLik(m.order, m.caps, h, g, d15Memo) : null;
        var pt = m.tri ? triLogLik(m.tri, h, g) : null;
        if (pl) l += pl.total;
        if (pd) l += pd.total;
        if (pt) l += pt.total;
        perG.push(l);
        perGparts.push({ plate: pl, d15: pd, tri: pt, g: g });
      }
      cells.push({ perG: perG, parts: perGparts });
      logs.push(h.logPrior + logSumExp(perG));
    }
    var norm = logSumExp(logs), post = [];
    for (i = 0; i < logs.length; i++) post.push(Math.exp(logs[i] - norm));

    var byType = { normal: 0, protan: 0, deutan: 0, tritan: 0 };
    var sevNum = { protan: 0, deutan: 0, tritan: 0 };
    for (i = 0; i < HYPOTHESES.length; i++) {
      h = HYPOTHESES[i];
      byType[h.type] += post[i];
      if (h.type !== 'normal') sevNum[h.type] += post[i] * h.severity;
    }
    var pDeficient = byType.protan + byType.deutan + byType.tritan;
    var pRedGreen = byType.protan + byType.deutan;

    /* Posterior means of the nuisance terms, over the joint (hypothesis × gain).
       The marginalisation already had to decide how inattentive and how
       sensitive this person was in order to explain the data; that answer is
       evidence about whether the sitting can be trusted, and discarding it is
       how someone tapping at random used to receive a diagnosis instead of
       "we could not measure you". */
    function nuisanceMean(field) {
      var num = 0, den = 0, a, b, c;
      for (a = 0; a < HYPOTHESES.length; a++) {
        var innerG = logSumExp(cells[a].perG);
        for (b = 0; b < SENS_GRID.length; b++) {
          var pp = field === 'gain' ? null : cells[a].parts[b][field];
          var wGa = post[a] * Math.exp(cells[a].perG[b] - innerG);
          if (field === 'gain') { num += wGa * SENS_GRID[b]; den += wGa; continue; }
          if (!pp || !pp.grid || pp.grid.length < 2) continue;
          var inner = logSumExp(pp.terms);
          for (c = 0; c < pp.grid.length; c++) {
            var q = Math.exp(pp.terms[c] - inner);
            num += wGa * q * pp.grid[c]; den += wGa * q;
          }
        }
      }
      return den > 0 ? num / den : null;
    }
    var eLapse = nuisanceMean('plate');
    var eTau = nuisanceMean('d15');
    var eMu = nuisanceMean('tri');
    var eGain = nuisanceMean('gain');

    var quality = 1;
    var missedDemo = false;
    if (m.answers) for (i = 0; i < m.answers.length; i++) if (m.answers[i].kind === 'demo' && !m.answers[i].correct) missedDemo = true;
    if (missedDemo) quality -= 0.45;

    /* Attention probes are pooled across the three axes, and the question asked
       of them is statistical, not a hand-picked count: is this many misses
       explainable as ordinary distraction? A fixed "2 or more" was tuned to nine
       probes and silently branded 5 % of ATTENTIVE observers unmeasurable, which
       cost real dichromats their result. */
    var catchDone = 0, catchFails = 0, unreliableAxes = 0;
    if (m.tri) for (i = 0; i < RG_TYPES.length; i++) {
      var t = m.tri[RG_TYPES[i]];
      if (!t) continue;
      catchDone += t.catchDone || 0; catchFails += t.catchFails || 0;
      if (t.reliable === false) unreliableAxes++;
    }
    var catchMissRate = catchDone ? catchFails / catchDone : null;
    var catchP = null;
    if (catchDone >= 5) {
      catchP = binomTailAtLeast(catchFails, catchDone, 0.06);
      if (catchP < 0.01) quality = Math.min(quality, 0.15);
      else if (catchP < 0.10) quality -= 0.18;
    } else if (unreliableAxes >= 2) quality -= 0.40;

    if (eLapse != null && eLapse > 0.09) quality -= Math.min(0.55, (eLapse - 0.09) * 3.0);
    if (eTau != null && eTau > 5.0) quality -= Math.min(0.30, (eTau - 5.0) * 0.08);
    var lowSensitivity = (eGain != null && eGain < SENS_TOO_LOW);
    if (lowSensitivity) quality = Math.min(quality, 0.18);

    var type, severity = 'none', inconclusive = false;
    if (byType.normal >= pDeficient) {
      type = 'normal';
    } else {
      /* Which deficiency? Protan and deutan cannot be separated chromatically
         on a screen — their confusion lines are close, and everything our
         plates can say about one, they say about the other (measured: the two
         "differentiating" plates collapse for BOTH types). Saying "red-green"
         when the evidence does not pick one is not a hedge, it is the strongest
         claim a screening test is entitled to make. */
      var top = 'protan';
      if (byType.deutan > byType[top]) top = 'deutan';
      if (byType.tritan > byType[top]) top = 'tritan';
      if (top !== 'tritan' && pRedGreen > 0 && byType[top] / pRedGreen < 0.72) type = 'redgreen';
      else type = top;
      var sevType = type === 'redgreen' ? (byType.protan > byType.deutan ? 'protan' : 'deutan') : type;
      var sevMean = byType[sevType] > 0 ? sevNum[sevType] / byType[sevType] : 0.5;
      severity = sevMean >= 0.82 ? 'strong' : sevMean >= 0.58 ? 'moderate' : 'mild';
    }

    quality = Math.max(0, Math.min(1, quality));
    var pWinner = type === 'normal' ? byType.normal : (type === 'redgreen' ? pRedGreen : byType[type]);
    var confidence = pWinner >= 0.85 ? 'high' : pWinner >= 0.62 ? 'moderate' : 'low';
    if (quality < 0.6) confidence = 'low';
    /* The bar for NAMING a deficiency rises as the sitting gets less
       trustworthy. The two errors are not symmetric: telling someone they are
       colour-blind on the strength of inattentive answers is the expensive one,
       and "try again" costs them two minutes. */
    var bar = 0.5 + 0.42 * (1 - quality);
    if ((pWinner < bar && type !== 'normal') || quality <= 0.2) { inconclusive = true; confidence = 'low'; }

    var sevKey = type === 'redgreen' ? (byType.protan > byType.deutan ? 'protan' : 'deutan') : type;
    return {
      type: type, severity: severity, confidence: confidence, inconclusive: inconclusive,
      posterior: byType, pDeficient: pDeficient, pWinner: pWinner, bar: bar,
      quality: quality, missedDemo: missedDemo, unreliableAxes: unreliableAxes,
      lowSensitivity: lowSensitivity,
      catchDone: catchDone, catchFails: catchFails, catchMissRate: catchMissRate, catchP: catchP,
      nuisance: { lapse: eLapse, tau: eTau, mu: eMu, gain: eGain },
      severityScore: type === 'normal' || !byType[sevKey] ? 0 : sevNum[sevKey] / byType[sevKey],
    };
  }

  /**
   * Per-plate explanation for the review screen: what this plate asks, who can
   * normally read it, and how visible its figure was to the observer we
   * concluded the person is. Pure data — the page decides how to phrase it.
   */
  function explainPlates(answers, verdictType) {
    var out = [], i;
    var who = (verdictType === 'redgreen' || verdictType === 'inconclusive') ? 'deutan' : verdictType;
    for (i = 0; i < answers.length; i++) {
      var a = answers[i];
      if (!a.fg || !a.bg) continue;
      var dNormal = dprimeParts(a.fg, a.bg, PLATE_ADAPT, 'normal', 0).chroma;
      var dYou = (who && who !== 'normal') ? dprimeParts(a.fg, a.bg, PLATE_ADAPT, who, 1).chroma : dNormal;
      out.push({
        plate: a.plate, digit: a.digit, answer: a.answer, correct: !!a.correct,
        kind: a.kind, target: a.type || null,
        dNormal: Math.round(dNormal * 10) / 10,
        dYou: Math.round(dYou * 10) / 10,
        // a figure this faint is not something the person "missed" — it is not there for them
        vanishesForYou: dYou < 2.5 && dNormal > 6,
      });
    }
    return out;
  }
  /* ===================================================================== */
  /* Export                                                                 */
  /* ===================================================================== */
  window.CBT = {
    ready: true,
    // pool / specs
    buildPool: buildPool, precompute: precompute, computeSpecs: computeSpecs,
    PLATE_SPECS: PLATE_SPECS, PLATE_SEQUENCE: PLATE_SEQUENCE, getSpecs: getSpecs, pickPair: pickPair,
    // plates
    SHAPES: SHAPES, drawShape: drawShape,
    buildMask: buildMask, packDots: packDots, renderPlate: renderPlate, validatePlate: validatePlate,
    makeRng: makeRng,
    // d15
    buildD15Caps: buildD15Caps, scoreD15: scoreD15, rgbToUv: rgbToUv,
    // trivector
    TRI_BG_UV: TRI_BG_UV, TRI_DIR: TRI_DIR, TRI_LUM_SET: TRI_LUM_SET,
    TRI_NORMAL: TRI_NORMAL, TRI_CUT: TRI_CUT,
    uvYToRgb: uvYToRgb, targetUv: targetUv, maxInGamutR: maxInGamutR,
    triBgRgb: triBgRgb, triTargetRgb: triTargetRgb, classifyTrivector: classifyTrivector,
    // vision model (cone contrasts -> cardinal channels -> d')
    CONE_SIGMA: CONE_SIGMA, conesSeen: conesSeen, dprimeParts: dprimeParts,
    PLATE_ADAPT: PLATE_ADAPT, D15_ADAPT: D15_ADAPT,
    // trivector axes + adaptive threshold
    TRI_AXES: TRI_AXES, triChooseY: triChooseY, triDprime: triDprime,
    triPredictedThreshold: triPredictedThreshold, makeZest: makeZest, ZEST_DEFAULTS: ZEST_DEFAULTS,
    // verdict
    assess: assess, explainPlates: explainPlates, HYPOTHESES: HYPOTHESES,
    VERDICT_PRIOR: VERDICT_PRIOR, SENS_GRID: SENS_GRID, setTriMuSigma: setTriMuSigma,
    combineVerdict: combineVerdict,
    // constants (for tests / transparency)
    THRESHOLDS: {
      NORMAL_MIN: NORMAL_MIN, CVD_MAX: CVD_MAX, CVD_MAX_MILD: CVD_MAX_MILD,
      HIDDEN_NORMAL_MAX: HIDDEN_NORMAL_MAX, HIDDEN_CVD_MIN: HIDDEN_CVD_MIN,
      JITTER_L: JITTER_L
    }
  };
})();
